import type { FunctionComponent } from 'react';
import { Inline, paletteHelper, Text } from '@property.com.au/ui-kit';
import styled, { css } from 'styled-components';
import { MediaCountsIcon } from '../MediaCountsIcon';
import type { ColorVariants, MediaCount } from '../../MediaCountButtons';
import { isNotNullOrUndefinedValue } from '../../../../../helpers/isNotNullOrUndefinedValue';

interface MediaCountContainerProps {
  variant: ColorVariants;
  $shouldChangeColorOnHover: boolean;
}

interface MediaCountButtonProps {
  count: MediaCount;
  onClickHandler?: (imageIndexFrom: number) => void;
  variant?: ColorVariants;
  ariaLabel?: string;
}

const variantStylingHelper = (variant: ColorVariants, shouldChangeColorOnHover: boolean) => {
  switch (variant) {
    case 'grey':
      return css`
        color: ${paletteHelper('textSecondary')};
      `;
    case 'white':
    default:
      return css`
        color: ${paletteHelper('textInverted')};

        ${shouldChangeColorOnHover &&
        css`
          &:hover {
            color: ${paletteHelper('textSecondary')};
          }
        `}
      `;
  }
};

const MediaCountContainer = styled(Inline)<MediaCountContainerProps>`
  ${({ variant, $shouldChangeColorOnHover }) =>
    variantStylingHelper(variant, $shouldChangeColorOnHover)}
`;

const ValueWrapper = styled(Text).attrs(() => ({ variant: 'body02' }))``;

const MediaButton = styled.button`
  &:hover {
    cursor: pointer;
  }
`;

export const MediaCountButton: FunctionComponent<MediaCountButtonProps> = ({
  count: { iconNameMd, title, value, imageIndexFrom },
  onClickHandler,
  variant = 'white',
  ariaLabel,
}) => {
  const onMediaCountClickGoesToFirstImageOfMediaType = isNotNullOrUndefinedValue(imageIndexFrom);

  return (
    <MediaButton
      onClick={onClickHandler ? () => onClickHandler(imageIndexFrom ?? 0) : () => undefined}
      aria-label={ariaLabel ?? `Go to ${title}`}
    >
      <MediaCountContainer
        grow={false}
        gap="twoExtraSmall"
        title={title}
        variant={variant}
        $shouldChangeColorOnHover={onMediaCountClickGoesToFirstImageOfMediaType}
      >
        <MediaCountsIcon iconName={iconNameMd} title={title} />
        <ValueWrapper>{value}</ValueWrapper>
      </MediaCountContainer>
    </MediaButton>
  );
};
